// client/src/components/pages/LexiStar/Reports/ReportRenderShell.tsx
//
// The chrome-free shell a report page renders in when it is opened with a
// one-time report token (…?print=1&report_session=<token>; ST-00188 M4-R2,
// m4-contracts §6). index.tsx mounts it INSTEAD of the app (no sidebar, no
// sockets, no /auth/me round-trip, no login redirect): it exchanges the token
// at POST /api/auth/report-session, installs the session for this page's API
// calls (reportSession.ts), provides the auth context the pages read (the
// session's permissions, else the caller's from GET /tenants/current), and
// routes to the report pages in print mode. Anything else is refused.

import { useEffect, useMemo, useRef, useState, type Context } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import api from '../../../../services/api';
import Certificate from '../Commissioning/Certificate';
import DailySummary from './DailySummary';
import StandardReport from './StandardReport';
import RunReport from './RunReport';
import TripReport from './TripReport';
import { PaperMessage, ReportFrame, ReportRenderContext } from './reportAtoms';
import type { LexiAuth } from '../../../../types/commonComponents';
import {
  clearReportSession,
  exchangeReportSession,
  installReportSession,
  reportSessionToken,
  stripReportSessionFromUrl,
  type ReportSession,
} from './reportSession';
import { errorText, isObj } from '../../../../services/lexistarReports';

type ShellAuth = LexiAuth & {
  authenticated: boolean;
  loading: boolean;
  contextReady: boolean;
  authEnabled: boolean;
  userPermissions: string[];
};

const Ctx = AuthContext as unknown as Context<ShellAuth | null>;

/** The caller's permissions when the session answer does not carry them. */
async function permissionsOf(session: ReportSession): Promise<{ permissions: string[]; tenant: ShellAuth['tenant'] }> {
  if (session.permissions) {
    return { permissions: session.permissions, tenant: (session.tenant as ShellAuth['tenant']) || null };
  }
  try {
    const res = await api.get('/tenants/current');
    const t = isObj(res.data) && isObj(res.data.data) ? res.data.data : null;
    const cu = t && isObj(t.currentUser) ? t.currentUser : null;
    const perms = cu && Array.isArray(cu.appPermissions) ? cu.appPermissions.map(String) : null;
    // No list at all: the server still enforces every call; the page shows what it is given.
    return { permissions: perms || ['*'], tenant: (t as ShellAuth['tenant']) || null };
  } catch {
    return { permissions: ['*'], tenant: null };
  }
}

function Refused() {
  return (
    <ReportFrame print state="error" error="not-a-report">
      <PaperMessage print title="This page cannot be rendered as a report." />
    </ReportFrame>
  );
}

export default function ReportRenderShell() {
  const [state, setState] = useState<{ phase: 'opening' | 'ready' | 'error'; auth: ShellAuth | null; error: string | null }>({
    phase: 'opening',
    auth: null,
    error: null,
  });

  // The exchange happens once per page load: the token is single-use, and React's
  // development StrictMode mounts effects twice.
  const opening = useRef<Promise<{ session: ReportSession; permissions: string[]; tenant: ShellAuth['tenant'] }> | null>(null);

  useEffect(() => {
    // The splash (index.html) belongs to the app, not to a document.
    document.getElementById('lb-splash')?.remove();
    (window as unknown as { __hideLexiBridgeLoader?: () => void }).__hideLexiBridgeLoader?.();

    if (!opening.current) {
      const token = reportSessionToken();
      stripReportSessionFromUrl();
      opening.current = (async () => {
        if (!token) throw new Error('No report session.');
        const session = await exchangeReportSession(token);
        installReportSession(session);
        return { session, ...(await permissionsOf(session)) };
      })();
    }
    let alive = true;
    opening.current
      .then(({ session, permissions, tenant }) => {
        if (!alive) return;
        installReportSession(session);
        const has = (key: string) => permissions.includes('*') || permissions.includes(key);
        setState({
          phase: 'ready',
          error: null,
          auth: {
            hasPermission: has,
            tenant,
            user: (session.user as ShellAuth['user']) || null,
            authenticated: true,
            loading: false,
            contextReady: true,
            authEnabled: true,
            userPermissions: permissions,
          },
        });
      })
      .catch((err) => {
        if (alive) setState({ phase: 'error', auth: null, error: errorText(err, (err as Error)?.message || 'The report session could not be opened.') });
      });
    return () => {
      alive = false;
      clearReportSession();
    };
  }, []);

  const routes = useMemo(
    () => (
      <Routes>
        <Route path="/lexistar/reports/trip/:trip_id" element={<TripReport />} />
        <Route path="/lexistar/reports/run/:run_id" element={<RunReport />} />
        <Route path="/lexistar/reports/daily/:date" element={<DailySummary />} />
        <Route path="/lexistar/reports/daily" element={<DailySummary />} />
        <Route path="/lexistar/reports/:key" element={<StandardReport />} />
        {/* The certificate PDF goes through the same path (m4-contracts §6). */}
        <Route path="/lexistar/commissioning/:id/certificate" element={<Certificate />} />
        <Route path="*" element={<Refused />} />
      </Routes>
    ),
    []
  );

  if (state.phase !== 'ready' || !state.auth) {
    return (
      <ReportFrame print state={state.phase === 'error' ? 'error' : 'loading'} error={state.error}>
        <PaperMessage print title={state.phase === 'error' ? state.error || 'The report session could not be opened.' : 'Opening the report…'} />
      </ReportFrame>
    );
  }

  return (
    <Ctx.Provider value={state.auth}>
      <ReportRenderContext.Provider value>
        <BrowserRouter>{routes}</BrowserRouter>
      </ReportRenderContext.Provider>
    </Ctx.Provider>
  );
}
